import React, { useEffect, useRef, useState } from "react";
import api, { API_BASE, getToken } from "@/lib/api";
import { toast } from "sonner";
import { UploadSimple, Trash, GridFour, BookOpen, Sparkle, Users, PencilSimple, MagicWand, Check, X } from "@phosphor-icons/react";
import { invalidateMediaCache } from "@/components/vansha/PhotoAttacher";
import { PolaroidIcon, SunBloom } from "@/components/vansha/Graphics";

async function compressImage(file) {
  if (!file.type.startsWith("image/") || file.type === "image/gif") return file;
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, 1920 / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale), h = Math.round(img.height * scale);
      const c = document.createElement("canvas");
      c.width = w; c.height = h;
      c.getContext("2d").drawImage(img, 0, 0, w, h);
      c.toBlob((b) => resolve(b && b.size < file.size ? new File([b], file.name.replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" }) : file), "image/jpeg", 0.85);
    };
    img.onerror = () => resolve(file);
    img.src = URL.createObjectURL(file);
  });
}

// Traverse a DataTransferItem folder tree → return all File objects
async function walkEntries(entries) {
  const files = [];
  const visit = async (entry) => {
    if (entry.isFile) {
      await new Promise((r) => entry.file((f) => { files.push(f); r(); }));
    } else if (entry.isDirectory) {
      const reader = entry.createReader();
      const kids = await new Promise((r) => reader.readEntries(r));
      for (const k of kids) await visit(k);
    }
  };
  for (const e of entries) if (e) await visit(e);
  return files;
}

export default function Memories() {
  const [items, setItems] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [people, setPeople] = useState([]);
  const [personFilter, setPersonFilter] = useState("");
  const [view, setView] = useState("grid");
  const [uploading, setUploading] = useState({ n: 0, total: 0 });
  const [caption, setCaption] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [editing, setEditing] = useState(null);
  const [tagInput, setTagInput] = useState("");
  const token = getToken();
  const fileRef = useRef(null);

  const load = async () => {
    const [m, a, p] = await Promise.all([
      api.get("/memories"),
      api.get("/memories/albums"),
      api.get("/memories/people"),
    ]);
    setItems(m.data); setAlbums(a.data); setPeople(p.data);
  };
  useEffect(() => { load(); }, []);

  const uploadFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading({ n: 0, total: files.length });
    let ok = 0;
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      let finalFile = file;
      if (file.type.startsWith("image/")) {
        try { finalFile = await compressImage(file); } catch (_) { finalFile = file; }
      }
      const fd = new FormData();
      fd.append("file", finalFile);
      try {
        await api.post(`/memories/upload?caption=${encodeURIComponent(caption)}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        ok++;
      } catch (e) { /* skip */ }
      setUploading({ n: i + 1, total: files.length });
    }
    if (ok) toast.success(`${ok}/${files.length} added`);
    setCaption("");
    if (fileRef.current) fileRef.current.value = "";
    setUploading({ n: 0, total: 0 });
    invalidateMediaCache();
    load();
  };

  const onDrop = async (e) => {
    e.preventDefault();
    setDragOver(false);
    const items = e.dataTransfer.items;
    if (!items || items.length === 0) {
      return uploadFiles(Array.from(e.dataTransfer.files || []));
    }
    // Support folder drops via webkitGetAsEntry
    const entries = [];
    for (const it of items) {
      const entry = it.webkitGetAsEntry ? it.webkitGetAsEntry() : null;
      if (entry) entries.push(entry);
    }
    const files = entries.length ? await walkEntries(entries) : Array.from(e.dataTransfer.files || []);
    uploadFiles(files.filter((f) => f.type.startsWith("image/") || f.type.startsWith("video/")));
  };

  const del = async (id) => { await api.delete(`/memories/${id}`); invalidateMediaCache(); load(); };
  const memoryUrl = (id) => `${API_BASE}/memories/file/${id}?auth=${token}`;

  const suggestCaption = async (m) => {
    toast.info("Vansha is looking at your photo…");
    try {
      const { data } = await api.post(`/memories/${m.id}/suggest-caption`);
      setEditing({ id: m.id, caption: data.caption, people_tags: m.people_tags || [] });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not suggest"); }
  };

  const saveEdit = async () => {
    if (!editing) return;
    try {
      await api.put(`/memories/${editing.id}/caption`, { caption: editing.caption });
      await api.put(`/memories/${editing.id}/tags`, { people_tags: editing.people_tags });
      toast.success("Saved.");
      setEditing(null);
      load();
    } catch { toast.error("Could not save."); }
  };

  const addTagToEdit = () => {
    if (!tagInput.trim() || !editing) return;
    if (editing.people_tags.includes(tagInput.trim())) return;
    setEditing({ ...editing, people_tags: [...editing.people_tags, tagInput.trim()] });
    setTagInput("");
  };

  const filtered = personFilter ? items.filter((m) => (m.people_tags || []).includes(personFilter)) : items;

  return (
    <div className="space-y-8" data-testid="memories-page">
      <header className="relative overflow-hidden vansha-card-tinted-blush p-8 sm:p-10 flex items-end justify-between flex-wrap gap-4">
        <div className="absolute right-0 -top-4 opacity-95 hidden lg:block pointer-events-none">
          <SunBloom size={200} />
        </div>
        <div className="absolute right-40 top-8 opacity-95 hidden lg:block pointer-events-none">
          <PolaroidIcon size={110} />
        </div>
        <div className="relative z-10">
          <p className="overline text-terracotta mb-2">Family memory vault</p>
          <h1 className="font-serif font-semibold text-4xl sm:text-5xl lg:text-6xl leading-none">
            Mem<em className="text-terracotta font-normal italic">ories</em>
          </h1>
          <p className="text-ink-700 mt-3 leading-relaxed max-w-xl">Photographs that should never be lost to time.</p>
        </div>
        <div className="flex gap-2 border border-ink-20 rounded-full p-1 relative z-10 bg-white/70 backdrop-blur" data-testid="view-toggle">
          <button onClick={() => setView("grid")} className={`text-sm px-4 py-1.5 rounded-full flex items-center gap-2 ${view === "grid" ? "bg-terracotta text-white" : "text-ink-700"}`} data-testid="view-grid"><GridFour weight="thin" size={14} /> Gallery</button>
          <button onClick={() => setView("album")} className={`text-sm px-4 py-1.5 rounded-full flex items-center gap-2 ${view === "album" ? "bg-terracotta text-white" : "text-ink-700"}`} data-testid="view-album"><BookOpen weight="thin" size={14} /> Albums</button>
        </div>
      </header>

      {/* Drop zone + upload */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`vansha-card p-6 border-2 border-dashed transition ${dragOver ? "border-terracotta bg-terracotta/5" : "border-ink-20"}`}
        data-testid="dropzone">
        <input ref={fileRef} type="file" accept="image/*,video/*" multiple onChange={(e) => uploadFiles(Array.from(e.target.files || []))} className="hidden" id="mem-file" data-testid="memory-file-input" />
        <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center">
          <input value={caption} onChange={(e) => setCaption(e.target.value)} className="input-line flex-1" placeholder="A caption (optional)…" data-testid="memory-caption" />
          <label htmlFor="mem-file" className={`btn-primary cursor-pointer ${uploading.total > 0 ? "opacity-60" : ""}`} data-testid="memory-upload-btn">
            <UploadSimple weight="thin" size={18} /> {uploading.total > 0 ? `Uploading ${uploading.n}/${uploading.total}…` : "Upload photos or videos"}
          </label>
        </div>
        <p className="text-xs text-ink-500 mt-3 text-center sm:text-left">Drag & drop photos, videos, or entire folders here. HEIC from iPhone auto-converts.</p>
      </div>

      {/* People filter */}
      {people.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="people-filter">
          <Users weight="thin" size={16} className="text-ink-500" />
          <span className="overline mr-2">People</span>
          <button onClick={() => setPersonFilter("")} className={`text-xs px-3 py-1 rounded-full ${!personFilter ? "bg-terracotta text-white" : "border border-ink-20 text-ink-700"}`} data-testid="filter-all">All</button>
          {people.map((p) => (
            <button key={p.name} onClick={() => setPersonFilter(p.name === personFilter ? "" : p.name)} className={`text-xs px-3 py-1 rounded-full ${personFilter === p.name ? "bg-terracotta text-white" : "border border-ink-20 text-ink-700"}`} data-testid={`filter-${p.name}`}>
              {p.name} · {p.count}
            </button>
          ))}
        </div>
      )}

      {view === "grid" ? (
        filtered.length === 0 ? (
          <div className="vansha-card p-12 text-center text-ink-500" data-testid="memories-empty">
            <p className="font-serif text-2xl text-ink-900 mb-2">Your vault awaits.</p>
            <p>Upload your first photo — or drop a folder above.</p>
          </div>
        ) : (
          <div className="columns-1 sm:columns-2 md:columns-3 gap-6 space-y-6" data-testid="memories-grid">
            {filtered.map((it) => <MemoryCard key={it.id} it={it} url={memoryUrl(it.id)} onDelete={del} onEdit={setEditing} onSuggest={suggestCaption} />)}
          </div>
        )
      ) : (
        <div className="space-y-10" data-testid="albums">
          {albums.map((a, i) => (
            <section key={i} data-testid={`album-${i}`}>
              <div className="flex items-baseline gap-3 mb-4">
                <Sparkle weight={a.kind === "timeline" ? "fill" : "thin"} size={18} className={a.kind === "timeline" ? "text-saffron" : "text-ink-500"} />
                <div>
                  <h2 className="font-serif text-2xl">{a.title}</h2>
                  <p className="overline">{a.date || `${a.memories.length} photos`} {a.date && `· ${a.memories.length} photos`}</p>
                </div>
              </div>
              <div className="columns-1 sm:columns-2 md:columns-3 gap-4 space-y-4">
                {a.memories.map((m) => <MemoryCard key={m.id} it={m} url={memoryUrl(m.id)} onDelete={del} onEdit={setEditing} onSuggest={suggestCaption} compact />)}
              </div>
            </section>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setEditing(null)} data-testid="edit-modal">
          <div className="bg-vansha max-w-lg w-full rounded-3xl p-8 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <p className="overline text-saffron mb-2">Edit memory</p>
            <h2 className="font-serif text-2xl mb-6">Caption & people</h2>
            <div>
              <label className="overline mb-2 block">Caption</label>
              <textarea value={editing.caption} onChange={(e) => setEditing({ ...editing, caption: e.target.value })} className="input-line font-serif" rows="3" data-testid="edit-caption" />
            </div>
            <div className="mt-6">
              <label className="overline mb-2 block">People in this photo</label>
              <div className="flex gap-2 mb-3">
                <input value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTagToEdit())} className="input-line flex-1" placeholder="Rohan, Daadi, baby…" data-testid="tag-input" />
                <button onClick={addTagToEdit} className="btn-secondary text-sm" data-testid="add-tag">Add</button>
              </div>
              <div className="flex flex-wrap gap-2">
                {editing.people_tags.map((t) => (
                  <button key={t} onClick={() => setEditing({ ...editing, people_tags: editing.people_tags.filter((x) => x !== t) })} className="text-sm px-3 py-1 rounded-full bg-terracotta text-white" data-testid={`tag-${t}`}>{t} ×</button>
                ))}
              </div>
            </div>
            <div className="mt-8 flex justify-end gap-3">
              <button onClick={() => setEditing(null)} className="btn-ghost">Cancel</button>
              <button onClick={saveEdit} className="btn-primary" data-testid="save-edit">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MemoryCard({ it, url, onDelete, onEdit, onSuggest, compact = false }) {
  const isVideo = it.content_type?.startsWith("video/");
  const token = getToken();
  const posterUrl = it.poster_memory_id ? `${API_BASE}/memories/file/${it.poster_memory_id}?auth=${token}` : undefined;
  return (
    <div className={`vansha-card overflow-hidden break-inside-avoid group relative ${compact ? "mb-4" : "mb-6"}`} data-testid={`memory-${it.id}`}>
      {isVideo ? <video src={url} poster={posterUrl} controls className="w-full block" /> : <img src={url} alt={it.caption || ""} className="w-full block" loading="lazy" />}
      {it.caption && <div className="p-4"><p className="font-serif text-lg leading-snug">{it.caption}</p><p className="overline mt-1">{new Date(it.created_at).toLocaleDateString()}</p></div>}
      {(it.people_tags?.length > 0) && (
        <div className="px-4 pb-3 flex flex-wrap gap-1" data-testid={`tags-${it.id}`}>
          {it.people_tags.map((t) => <span key={t} className="text-xs px-2 py-0.5 rounded-full bg-vansha-accent text-ink-700">{t}</span>)}
        </div>
      )}
      <div className="absolute top-3 right-3 flex gap-2 opacity-0 group-hover:opacity-100 transition">
        {!isVideo && (
          <button onClick={() => onSuggest(it)} className="bg-white/90 backdrop-blur p-2 rounded-full" title="AI suggest caption" data-testid={`suggest-${it.id}`}>
            <MagicWand weight="thin" size={16} className="text-forest" />
          </button>
        )}
        <button onClick={() => onEdit({ id: it.id, caption: it.caption || "", people_tags: it.people_tags || [] })} className="bg-white/90 backdrop-blur p-2 rounded-full" title="Edit" data-testid={`edit-${it.id}`}>
          <PencilSimple weight="thin" size={16} className="text-ink-900" />
        </button>
        <button onClick={() => onDelete(it.id)} className="bg-white/90 backdrop-blur p-2 rounded-full" title="Delete" data-testid={`delete-memory-${it.id}`}>
          <Trash weight="thin" size={16} className="text-terracotta" />
        </button>
      </div>
    </div>
  );
}
